import {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import getWaitingIncomingCalls from './getWaitingIncomingCalls';
import pruneAuthorizedWaitingCallIds from './pruneAuthorizedWaitingCallIds';

export default function useWaitingCallsQueue({
  incomingPhones,
  activeCall,
  canReceiveCalls,
  onDismiss
}) {
  const [authorizedCallIds, setAuthorizedCallIds] = useState([]);

  const waitingCalls = useMemo(
    () => getWaitingIncomingCalls(incomingPhones, activeCall),
    [incomingPhones, activeCall]
  );

  const waitingCallIdsKey = useMemo(
    () => waitingCalls.map((call) => call.callId).join(','),
    [waitingCalls]
  );

  const visibleCount = useMemo(
    () => waitingCalls.filter((call) => authorizedCallIds.includes(call.callId)).length,
    [waitingCalls, authorizedCallIds]
  );

  const authorizedCalls = useMemo(
    () => waitingCalls.filter((call) => authorizedCallIds.includes(call.callId)),
    [waitingCalls, authorizedCallIds]
  );

  const handleAuthorizationResolved = useCallback((callId, isAuthorized) => {
    setAuthorizedCallIds((prev) => {
      const without = prev.filter((id) => id !== callId);
      return isAuthorized ? [...without, callId] : without;
    });
  }, []);

  // Keep authorizations for calls still in the queue. Clearing the whole list
  // would drop already-authorized rows: WaitingIncomingCallAuthorizer only
  // reports once per mount (reportedRef), so a wipe left only newly added calls
  // visible (e.g. badge stuck at 1 while 2 were waiting).
  useEffect(() => {
    setAuthorizedCallIds((prev) => pruneAuthorizedWaitingCallIds(prev, waitingCallIdsKey));
  }, [waitingCallIdsKey]);

  useEffect(() => {
    if (canReceiveCalls) return undefined;
    waitingCalls.forEach((call) => onDismiss?.(call.callId));
    return undefined;
  }, [canReceiveCalls, waitingCalls, onDismiss]);

  return {
    waitingCalls,
    waitingCallIdsKey,
    visibleCount,
    authorizedCalls,
    handleAuthorizationResolved,
    resetAuthorizedOnQueueChange: waitingCallIdsKey
  };
}
